import axios from 'axios'
import type { ApiErrorBody, ApiErrorCode } from '@milliswift/types'

/**
 * A failed API call, normalised from the platform's error envelope.
 *
 * The platform always returns `{ error, message, trace_id? }`. `trace_id` is
 * what support asks for, so it is preserved rather than swallowed, and it is
 * shown alongside generic failure states.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode | string
  readonly traceId: string | undefined
  /** The request never reached the server. */
  readonly isNetworkError: boolean

  constructor(options: {
    message: string
    status: number
    code: string
    traceId?: string | undefined
    isNetworkError?: boolean
  }) {
    super(options.message)
    this.name = 'ApiError'
    this.status = options.status
    this.code = options.code
    this.traceId = options.traceId
    this.isNetworkError = options.isNetworkError ?? false
  }

  /** Turn anything thrown by axios into an ApiError. */
  static from(error: unknown): ApiError {
    if (error instanceof ApiError) return error

    if (axios.isAxiosError(error)) {
      // No response at all: offline, DNS, CORS, or the server is down.
      if (!error.response) {
        return new ApiError({
          message: 'Could not reach the server. Check your connection and try again.',
          status: 0,
          code: 'network_error',
          isNetworkError: true,
        })
      }

      const { status, data } = error.response
      const body = (data ?? {}) as Partial<ApiErrorBody>

      return new ApiError({
        message: body.message || body.error || defaultMessageForStatus(status),
        status,
        code: body.error ?? 'unknown_error',
        traceId: body.trace_id,
      })
    }

    if (error instanceof Error) {
      return new ApiError({ message: error.message, status: 0, code: 'unknown_error' })
    }

    return new ApiError({
      message: 'Something went wrong. Please try again.',
      status: 0,
      code: 'unknown_error',
    })
  }
}

function defaultMessageForStatus(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'That item could not be found.'
  if (status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (status === 501) return 'This feature is not available yet.'
  if (status >= 500) return 'The server ran into a problem. Please try again shortly.'
  return 'Something went wrong. Please try again.'
}

/**
 * Replaces terse server messages with copy that makes sense to a person.
 * Falls through to the server's own message when a code is not listed, so an
 * unmapped error still says something specific.
 */
const FRIENDLY_MESSAGES: Partial<Record<string, string>> = {
  invalid_credentials: 'That email and password combination does not match our records.',
  invalid_token: 'Your session has expired. Please sign in again.',
  email_already_registered:
    'An account with that email already exists. Try signing in instead.',
  validation_error: 'Please check the highlighted fields and try again.',
  rate_limit_exceeded: 'Too many attempts. Please wait a few minutes and try again.',
  not_pending: 'That request has already been resolved.',
  quota_exceeded: 'You have reached your plan limit. Upgrade to continue.',
  no_capacity: 'No capacity is available right now. Please try again shortly.',
  not_ready: 'The service is starting up. Please try again in a moment.',
  manual_reapproval_required:
    'This device needs to be paired again. Start a new pairing from the device.',
  forbidden: 'You do not have permission to do that.',
  not_found: 'That item could not be found.',
  invalid_auth_key: 'That API key is not valid. Check it and try again.',
}

export function getApiErrorMessage(error: unknown): string {
  const apiError = ApiError.from(error)
  return FRIENDLY_MESSAGES[apiError.code] ?? apiError.message
}

/** True when retrying the same request could plausibly succeed. */
export function isRetryable(error: unknown): boolean {
  const apiError = ApiError.from(error)
  if (apiError.isNetworkError) return true
  return apiError.status === 429 || apiError.status >= 500
}
